import React, { useRef, useEffect, useState } from 'react';
import { StyleSheet, View, Text, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

/**
 * Componente de mapa Leaflet que muestra puntos de misión
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.points - Lista de puntos de la misión
 */
const SimpleMapView = ({ points }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  
  // Inicializar mapa Leaflet
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    
    // Cargar Leaflet dinámicamente
    const loadLeaflet = async () => {
      if (typeof window !== 'undefined' && !window.L) {
        // Cargar CSS de Leaflet
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
        
        // Cargar JS de Leaflet
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => initializeMap();
        document.head.appendChild(script);
      } else if (window.L) {
        initializeMap();
      }
    };
    
    const initializeMap = () => {
      if (!window.L || mapInstanceRef.current) return;
      
      // Crear mapa
      const map = window.L.map(mapRef.current, {
        center: [-33.508, -70.721],
        zoom: 15,
        zoomControl: true
      });
      
      // Añadir capa de tiles satelital
      window.L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: 'Tiles © Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
      }).addTo(map);
      
      mapInstanceRef.current = map;
      
      // Añadir puntos si ya están disponibles
      if (points && points.length > 0) {
        addPointsToMap(map, points);
      }
    };
    
    loadLeaflet();
    
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);
  
  // Añadir puntos al mapa cuando cambien
  useEffect(() => {
    if (mapInstanceRef.current && points && points.length > 0) {
      addPointsToMap(mapInstanceRef.current, points);
    }
  }, [points]);
  
  const addPointsToMap = (map, points) => {
    // Limpiar marcadores existentes
    map.eachLayer((layer) => {
      if (layer instanceof window.L.Marker || layer instanceof window.L.Polyline) {
        map.removeLayer(layer);
      }
    });
    
    if (!points || points.length === 0) return;
    
    const sortedPoints = [...points].sort((a, b) => a.order - b.order);
    const latLngs = [];
    
    // Añadir marcadores para cada punto
    sortedPoints.forEach((point, index) => {
      if (!point.location?.coordinates) return;
      
      const lat = point.location.coordinates[1];
      const lng = point.location.coordinates[0];
      
      if (isNaN(lat) || isNaN(lng)) return;
      
      latLngs.push([lat, lng]);
      
      // Crear icono personalizado con número
      const icon = window.L.divIcon({
        className: 'custom-marker',
        html: `<div style="
          background: #e74c3c;
          color: white;
          border-radius: 50%;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ">${point.order}</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });
      
      const marker = window.L.marker([lat, lng], { icon })
        .addTo(map)
        .bindPopup(`
          <div style="min-width: 200px;">
            <h4 style="margin: 0 0 8px 0; color: #2c3e50;">${point.name || `Punto ${point.order}`}</h4>
            <p style="margin: 4px 0; font-size: 12px;"><strong>Orden:</strong> ${point.order}</p>
            <p style="margin: 4px 0; font-size: 12px;"><strong>Altitud:</strong> ${point.altitude || 'N/A'} m</p>
            <p style="margin: 4px 0; font-size: 12px;"><strong>Coordenadas:</strong> ${lat.toFixed(6)}, ${lng.toFixed(6)}</p>
            ${point.description ? `<p style="margin: 4px 0; font-size: 12px;"><strong>Descripción:</strong> ${point.description}</p>` : ''}
          </div>
        `);
    });
    
    // Añadir línea conectando los puntos
    if (latLngs.length > 1) {
      window.L.polyline(latLngs, {
        color: '#e74c3c',
        weight: 3,
        opacity: 0.7
      }).addTo(map);
    }
    
    // Ajustar vista para mostrar todos los puntos
    if (latLngs.length > 0) {
      const bounds = window.L.latLngBounds(latLngs);
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  };

  
  return (
    <View style={styles.container}>
      <div ref={mapRef} style={styles.mapContainer} />
      
      {!points || points.length === 0 ? (
        <View style={styles.noPointsOverlay}>
          <Text style={styles.noPointsText}>No hay puntos para mostrar en el mapa</Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    width: '100%',
    height: 400,
    marginVertical: 10,
  },
  mapContainer: {
    width: '100%',
    height: '100%',
    borderRadius: 8,
    border: '1px solid #ddd',
    overflow: 'hidden',
  },
  noPointsOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
  },
  noPointsText: {
    color: '#7f8c8d',
    fontSize: 16,
    textAlign: 'center',
  },
});

export default SimpleMapView;